import { useMemo, type ReactNode } from 'react';
import { FeatureFlagsProvider } from '@vaptmenu/feature-flags-react';
import { useAuthStore } from '@/stores/auth';

const ENDPOINT = '/api/v1/platform/me/feature-flags';

/**
 * Sem sessão, a busca não vai à rede: a rota exige o token de plataforma e
 * só responderia 401. Devolver um mapa vazio também apaga as flags de quem
 * acabou de sair.
 */
const fetchWithoutSession: typeof fetch = async () =>
  new Response(JSON.stringify({ flags: {} }), {
    status: 200,
    headers: { 'Content-Type': 'application/json' },
  });

/**
 * Entrega ao backoffice as flags `platform.*` e as transversais.
 *
 * A rota `/platform/me/feature-flags` autentica pelo Bearer do operador, e o
 * `fetch` padrão da biblioteca não manda `Authorization`: montado direto, o
 * provider levava 401 em toda busca e toda flag lia false. Aqui o `fetcher`
 * anexa o token da sessão.
 *
 * Segue o PanelFeatureFlagsProvider, com três diferenças deliberadas:
 *
 * - O provider fica sempre montado, sem `key`. Trocar o tipo ou a `key` do
 *   elemento remonta a aplicação inteira, e a tela de enroll de MFA recebe os
 *   tokens no mesmo passo em que mostra os recovery codes, que aparecem uma
 *   vez só: remontar ali apagaria os códigos da tela. A busca é refeita
 *   porque o `fetcher` muda de identidade junto com o token, e a biblioteca
 *   busca de novo quando isso acontece.
 * - Renovar o token também refaz a busca. O token de plataforma vale 5
 *   minutos e fica salvo; ao recarregar a página ele costuma estar vencido, a
 *   primeira busca leva 401, e a renovação feita pela primeira chamada da tela
 *   é o sinal para tentar de novo.
 * - O `fetcher` não passa pelo cliente axios de `lib/api.ts`. O interceptor
 *   dele renova a sessão quando recebe 401, e a consulta periódica de 5
 *   minutos viraria um keep-alive: a janela de 4 h de inatividade da sessão
 *   de plataforma nunca venceria. Só a atividade do operador renova o token.
 */
export function PlatformFeatureFlagsProvider({ children }: { children: ReactNode }) {
  const accessToken = useAuthStore((s) => s.accessToken);

  const fetcher = useMemo<typeof fetch>(() => {
    if (!accessToken) return fetchWithoutSession;
    return (input, init) => {
      const headers = new Headers(init?.headers);
      headers.set('Authorization', `Bearer ${accessToken}`);
      return fetch(input, { ...init, headers });
    };
  }, [accessToken]);

  return (
    <FeatureFlagsProvider endpoint={ENDPOINT} fetcher={fetcher}>
      {children}
    </FeatureFlagsProvider>
  );
}
